import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Modal, Spinner, Table, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { clientAPI, quotationAPI } from '../../services/api';

const GenerateQuotation = () => {
  const [clients, setClients] = useState([]);
  const [formData, setFormData] = useState({
    clientId: '',
    projectTitle: '',
    requirements: '',
    features: ''
  });
  const [generatedQuotation, setGeneratedQuotation] = useState(null);
  const [consistencyCheck, setConsistencyCheck] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showConsistencyWarning, setShowConsistencyWarning] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await clientAPI.getAll();
      setClients(response.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setError('');
    setGenerating(true);

    try {
      const response = await quotationAPI.generate({
        clientId: formData.clientId,
        projectTitle: formData.projectTitle,
        requirements: formData.requirements,
        features: formData.features
      });
      
      setGeneratedQuotation(response.data);
      
      // Check price consistency
      const consistencyResponse = await quotationAPI.checkConsistency({
        clientId: formData.clientId,
        projectTitle: formData.projectTitle,
        requirements: formData.requirements,
        items: response.data.items,
        grandTotal: response.data.grandTotal
      });
      
      setConsistencyCheck(consistencyResponse.data);
      
      if (consistencyResponse.data.warning) {
        setShowConsistencyWarning(true);
      } else {
        setShowPreview(true);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate quotation');
    } finally {
      setGenerating(false);
    }
  };

  const handleProceed = () => {
    setShowConsistencyWarning(false);
    setShowPreview(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await quotationAPI.save({
        ...generatedQuotation,
        aiSummary: consistencyCheck?.aiSummary || ''
      });
      navigate('/quotations');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save quotation');
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col>
          <h2 className="mb-0">
            <i className="bi bi-calculator me-2"></i>
            Generate AI Quotation
          </h2>
        </Col>
      </Row>

      <Row>
        <Col md={8}>
          <Card className="mb-4">
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              
              <Form onSubmit={handleGenerate}>
                <Form.Group className="mb-3">
                  <Form.Label>Select Client *</Form.Label>
                  <Form.Select
                    name="clientId"
                    value={formData.clientId}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Choose a client...</option>
                    {clients.map((client) => (
                      <option key={client._id} value={client._id}>
                        {client.clientName} - {client.companyName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Project Title *</Form.Label>
                  <Form.Control
                    type="text"
                    name="projectTitle"
                    value={formData.projectTitle}
                    onChange={handleChange}
                    placeholder="Enter project title"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Project Requirements *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="requirements"
                    value={formData.requirements}
                    onChange={handleChange}
                    placeholder="Describe the project requirements..."
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Features *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="features"
                    value={formData.features}
                    onChange={handleChange}
                    placeholder="List the features needed..."
                    required
                  />
                </Form.Group>

                <Button 
                  variant="primary" 
                  type="submit" 
                  disabled={generating}
                >
                  {generating ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-magic me-2"></i>
                      Generate Quotation
                    </>
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Consistency Warning Modal */}
      <Modal show={showConsistencyWarning} onHide={() => setShowConsistencyWarning(false)} size="lg">
        <Modal.Header closeButton className="bg-warning text-dark">
          <Modal.Title>
            <i className="bi bi-exclamation-triangle me-2"></i>
            Price Consistency Warning
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {consistencyCheck && (
            <div>
              <Alert variant="warning">
                <h5>Similar Quotation Found</h5>
                <p>A similar quotation exists with {consistencyCheck.similarity}% similarity.</p>
              </Alert>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Card className="bg-light">
                    <Card.Body>
                      <h6>Previous Quotation</h6>
                      <h4 className="text-primary">{formatCurrency(consistencyCheck.previousQuotation.grandTotal)}</h4>
                      <small className="text-muted">
                        {new Date(consistencyCheck.previousQuotation.createdAt).toLocaleDateString()}
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="bg-light">
                    <Card.Body>
                      <h6>Current Quotation</h6>
                      <h4 className={consistencyCheck.percentageChange > 0 ? 'text-danger' : 'text-success'}>
                        {formatCurrency(consistencyCheck.currentQuotation.grandTotal)}
                      </h4>
                      <Badge bg={consistencyCheck.percentageChange > 0 ? 'danger' : 'success'}>
                        {consistencyCheck.percentageChange > 0 ? '+' : ''}
                        {consistencyCheck.percentageChange}% 
                        ({formatCurrency(consistencyCheck.difference)})
                      </Badge>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <h6>Item-wise Comparison:</h6>
              <Table size="sm" className="mb-3">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Previous</th>
                    <th>Current</th>
                    <th>Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {consistencyCheck.itemComparisons.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.category}</td>
                      <td>{formatCurrency(item.previousCost)}</td>
                      <td>{formatCurrency(item.currentCost)}</td>
                      <td className={item.difference > 0 ? 'price-increase' : 'price-decrease'}>
                        {item.difference > 0 ? '+' : ''}
                        {formatCurrency(item.difference)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <Alert variant="info">
                <strong>AI Summary:</strong>
                <p className="mb-0 mt-2">{consistencyCheck.aiSummary}</p>
              </Alert>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConsistencyWarning(false)}>
            Review Pricing
          </Button>
          <Button variant="primary" onClick={handleProceed}>
            Yes, Continue
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Preview Modal */}
      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Generated Quotation Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="preview-content">
          {generatedQuotation && (
            <div>
              <h4>{generatedQuotation.projectTitle}</h4>
              <hr />
              
              <Table bordered>
                <thead className="table-dark">
                  <tr>
                    <th>Category</th>
                    <th>Description</th>
                    <th className="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {generatedQuotation.items.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.category}</td>
                      <td>{item.description}</td>
                      <td className="text-end">{formatCurrency(item.cost)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="table-group-divider">
                  <tr>
                    <td colSpan="2" className="text-end"><strong>Subtotal:</strong></td>
                    <td className="text-end">{formatCurrency(generatedQuotation.subtotal)}</td>
                  </tr>
                  <tr>
                    <td colSpan="2" className="text-end"><strong>GST (18%):</strong></td>
                    <td className="text-end">{formatCurrency(generatedQuotation.gst)}</td>
                  </tr>
                  <tr className="table-primary">
                    <td colSpan="2" className="text-end"><strong>Grand Total:</strong></td>
                    <td className="text-end"><strong>{formatCurrency(generatedQuotation.grandTotal)}</strong></td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Edit
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : 'Save Quotation'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default GenerateQuotation;
